import { useEffect, useState } from "react";
import { Skeleton } from "@mui/material";
import dayjs from "dayjs";
import { DriverPeriodFilter } from "components/DriverPeriodFilter";
import { DriverCard, CardRow, DriverListSkeleton, EmptyState } from "components/DriverCard";
import { PageTitle } from "components/PageTitle";
import { driverDashboardService } from "services/driverDashboardService";
import { IDriverPunctuality, IDriverPunctualityOrder } from "interfaces/IDriverDashboard";
import { SummaryRow, SummaryValue, SummaryLabel, Badge } from "./styles";

const TODAY = dayjs().format("YYYY-MM-DD");

const LATE_STYLE: Record<string, { label: string; color: string }> = {
  "00. sem entrega": { label: "Sem entrega", color: "#A2A2A2" },
  "01. cancelado": { label: "Cancelado", color: "#A2A2A2" },
  "02. adiantado": { label: "Adiantado", color: "#3887FF" },
  "03. no prazo": { label: "No prazo", color: "#04D361" },
  "04. atrasado": { label: "Atrasado", color: "#EB001B" },
};

function LateBadge({ late }: { late: string }) {
  const style = LATE_STYLE[late] ?? { label: late, color: "#A2A2A2" };
  return <Badge color={style.color}>{style.label}</Badge>;
}

function SummarySkeleton() {
  return <Skeleton variant="text" width={48} sx={{ margin: "0 auto", fontSize: "1.4rem" }} />;
}

export function DriverPunctuality() {
  const [startDate, setStartDate] = useState(TODAY);
  const [endDate, setEndDate] = useState(TODAY);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<IDriverPunctuality | null>(null);

  useEffect(() => {
    setLoading(true);
    driverDashboardService
      .getMyPunctuality({ startDate, endDate })
      .then(setData)
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  return (
    <div aria-busy={loading}>
      <PageTitle>Pontualidade</PageTitle>

      <DriverPeriodFilter
        startDate={startDate}
        endDate={endDate}
        onChange={(start, end) => {
          setStartDate(start);
          setEndDate(end);
        }}
        disabled={loading}
      />

      <DriverCard>
        <SummaryRow>
          <div>
            <SummaryValue>{loading ? <SummarySkeleton /> : `${data?.punctuality ?? 0}%`}</SummaryValue>
            <SummaryLabel>Pontualidade</SummaryLabel>
          </div>
          <div>
            <SummaryValue>{loading ? <SummarySkeleton /> : data?.onTimeOrders ?? 0}</SummaryValue>
            <SummaryLabel>No prazo</SummaryLabel>
          </div>
          <div>
            <SummaryValue>{loading ? <SummarySkeleton /> : data?.totalOrders ?? 0}</SummaryValue>
            <SummaryLabel>Total de entregas</SummaryLabel>
          </div>
        </SummaryRow>
      </DriverCard>

      {loading ? (
        <DriverListSkeleton cards={3} rows={4} />
      ) : (
        <>
          {(data?.orders?.length ?? 0) === 0 && (
            <EmptyState>Nenhuma entrega no período selecionado.</EmptyState>
          )}
          {data?.orders?.map((order: IDriverPunctualityOrder) => (
            <DriverCard key={`${order.orderId}-${order.dt}`} title={`Pedido ${order.orderId}`}>
              <CardRow>
                <span>Data</span>
                <strong>{dayjs(order.dt).format("DD/MM/YYYY")}</strong>
              </CardRow>
              <CardRow>
                <span>Janela prevista</span>
                <strong>
                  {dayjs(order.scheduledFrom).format("HH:mm")} - {dayjs(order.scheduledTo).format("HH:mm")}
                </strong>
              </CardRow>
              {order.attemptTime && (
                <CardRow>
                  <span>Chegada</span>
                  <strong>{dayjs(order.attemptTime).format("HH:mm")}</strong>
                </CardRow>
              )}
              <CardRow>
                <span>Status</span>
                <LateBadge late={order.late} />
              </CardRow>
            </DriverCard>
          ))}
        </>
      )}
    </div>
  );
}

export default DriverPunctuality;